/**
 * One todo line: tappable checkbox circle + title, plus a trailing pencil
 * that opens the edit form. The toggle area and the pencil are SIBLING
 * touchables (never nested) so hits are unambiguous on web and native.
 * Completed rows fill the circle with the solid success token and pick the
 * check ink via onColor (solid fill — safe; see onColor docs).
 *
 * The circle is two layers in one 24pt box: a ring (the border) under a fill
 * disc (success background + checkmark). Completing a row springs the whole
 * circle back up from a dip (the "completion pop"). Un-completing drains the
 * fill: the disc shrinks back to that dip size and fades while the ring stays
 * put, then unmounts — the check visibly sinks out of the circle instead of
 * blinking off. Neither layer is a pressable, so the row keeps exactly two
 * top-level pressables (toggle + pencil).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  StyleSheet,
  Animated,
  Easing,
} from 'react-native';
import { Text } from '@/components/common/text';
import Icon from 'react-native-vector-icons/Ionicons';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useRadius } from '@/hooks/shared/useRadius';
import { useMotion } from '@/hooks/shared/useMotion';
import { useReducedMotion } from '@/hooks/shared/useReducedMotion';
import { useHoverStyle } from '@/hooks/shared/useHoverStyle';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { onColor } from '@/constants/shared/theme';
import type { ResidentTodo } from '@/types/residents/todos';

/**
 * How far the circle dips before springing back, and how small the fill gets
 * as it drains. Deep enough to read as a punch at 24pt, shallow enough that
 * the checkmark never looks like it blinks.
 */
const POP_FROM_SCALE = 0.6;

interface TodoRowProps {
  todo: ResidentTodo;
  isLast: boolean;
  onToggle: (id: string) => void;
  onEdit: (todo: ResidentTodo) => void;
  /** Optional right-aligned date (overdue rows show how old the item is). */
  dateLabel?: string;
}

export const TodoRow: React.FC<TodoRowProps> = React.memo(
  ({ todo, isLast, onToggle, onEdit, dateLabel }) => {
    const type = useTypography();
    const space = useSpacing();
    const radius = useRadius();
    const motion = useMotion();
    const reduceMotion = useReducedMotion();

    const popScale = useRef(new Animated.Value(1)).current;
    // 1 = the fill whole, 0 = drained away. Drives the disc's opacity and scale.
    const fillProgress = useRef(new Animated.Value(1)).current;
    const fillScale = useMemo(
      () =>
        fillProgress.interpolate({
          inputRange: [0, 1],
          outputRange: [POP_FROM_SCALE, 1],
        }),
      [fillProgress],
    );
    // Tracks the previous `completed` so the pop plays on the flip, not on
    // mount — otherwise every already-done row would punch as the list appears.
    const wasCompleted = useRef(todo.completed);

    // The fill outlives `completed` on the way out so it can drain in place.
    // Synced during render, not in an effect, so a check draws the fill in the
    // frame the store flips and reduced motion drops it just as fast; otherwise
    // the drain's end callback clears it.
    const [fillShown, setFillShown] = useState(todo.completed);
    if (todo.completed && !fillShown) setFillShown(true);
    if (!todo.completed && fillShown && reduceMotion) setFillShown(false);

    useEffect(() => {
      const justCompleted = todo.completed && !wasCompleted.current;
      const justUncompleted = !todo.completed && wasCompleted.current;
      wasCompleted.current = todo.completed;

      if (justUncompleted && !reduceMotion) {
        popScale.setValue(1);
        const drain = Animated.timing(fillProgress, {
          toValue: 0,
          duration: motion.duration.fast,
          easing: Easing.bezier(...motion.easing.standard),
          useNativeDriver: true,
        });
        drain.start(({ finished }) => {
          if (finished) setFillShown(false);
        });
        return () => drain.stop();
      }

      // Whole again: a check may interrupt a drain partway, so the fill lands
      // on full size here before anything else happens.
      fillProgress.setValue(1);

      // Reduced motion, or a row that mounted already done: land on the
      // resting size immediately and cancel anything in flight.
      if (!justCompleted || reduceMotion) {
        popScale.setValue(1);
        return;
      }

      // Known quirk, accepted: a background refetch that flips a row to done
      // pops it too. We deliberately do not try to tell that apart from a tap.
      popScale.setValue(POP_FROM_SCALE);
      const pop = Animated.spring(popScale, {
        toValue: 1,
        useNativeDriver: true,
        ...motion.spring.bouncy,
      });
      pop.start();
      return () => pop.stop();
    }, [todo.completed, reduceMotion, popScale, fillProgress, motion]);

    // A finished drain leaves the value at 0. Park it whole once the disc has
    // gone, so the next check mounts the fill full-size from its first frame
    // rather than a frame later when the effect above runs.
    useEffect(() => {
      if (!fillShown) fillProgress.setValue(1);
    }, [fillShown, fillProgress]);

    // One hover band for the whole row: the toggle and the pencil are two
    // pressables, and tinting each on its own reads as two half-rows. The row
    // View owns the tint (mouse events, web only); the pressables keep only
    // the cursor and their press dip.
    const rowHover = useHoverStyle('row');

    const styles = useThemedStyles(({ colors, semantic }) =>
      StyleSheet.create({
        row: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: space.md,
          paddingVertical: space.lg,
        },
        rowBorder: {
          borderBottomWidth: 1,
          borderBottomColor: colors.separator,
        },
        toggleArea: {
          flex: 1,
          flexDirection: 'row',
          alignItems: 'center',
          gap: space.md,
        },
        checkbox: {
          width: 24,
          height: 24,
        },
        ring: {
          ...StyleSheet.absoluteFillObject,
          borderRadius: radius.full,
          borderWidth: 2,
          borderColor: semantic.border.input,
        },
        // Green under a whole fill, so the disc's anti-aliased edge never
        // blends against grey. It flips back with the store — while the disc
        // still covers it — so the drain uncovers the resting grey ring.
        ringDone: {
          borderColor: semantic.status.success,
        },
        fill: {
          ...StyleSheet.absoluteFillObject,
          borderRadius: radius.full,
          backgroundColor: semantic.status.success,
          alignItems: 'center',
          justifyContent: 'center',
        },
        checkmark: {
          ...type.captionMedium,
          color: onColor(semantic.status.success),
        },
        title: {
          ...type.body,
          flex: 1,
          color: colors.text.primary,
        },
        titleDone: {
          color: colors.text.muted,
          textDecorationLine: 'line-through',
        },
        dateLabel: {
          ...type.small,
          // secondary, not muted: this is informational text (how overdue an
          // item is), and muted is 4.48:1 on the light card — under the 4.5:1
          // AA floor for 13pt. secondary clears it in both themes.
          color: colors.text.secondary,
        },
        editIcon: {
          color: colors.text.muted,
        },
      }),
    );

    return (
      <View
        style={[styles.row, !isLast && styles.rowBorder, rowHover.viewStyle]}
        {...(rowHover.mouseProps as any)}
      >
        <Hoverable
          hover="none"
          style={styles.toggleArea}
          onPress={() => onToggle(todo.id)}
          accessibilityRole="checkbox"
          accessibilityState={{ checked: todo.completed }}
          accessibilityLabel={todo.title}
        >
          <Animated.View
            style={[styles.checkbox, { transform: [{ scale: popScale }] }]}
          >
            <View style={[styles.ring, todo.completed && styles.ringDone]} />
            {fillShown ? (
              <Animated.View
                style={[
                  styles.fill,
                  { opacity: fillProgress, transform: [{ scale: fillScale }] },
                ]}
              >
                <Text style={styles.checkmark}>✓</Text>
              </Animated.View>
            ) : null}
          </Animated.View>
          <Text
            style={[styles.title, todo.completed && styles.titleDone]}
            numberOfLines={1}
          >
            {todo.title}
          </Text>
          {dateLabel ? (
            <Text style={styles.dateLabel} numberOfLines={1}>
              {dateLabel}
            </Text>
          ) : null}
        </Hoverable>
        <Hoverable
          hover="none"
          onPress={() => onEdit(todo)}
          // 16pt icon + 14 slop per side = the 44pt minimum target.
          hitSlop={{ top: 14, bottom: 14, left: 14, right: 14 }}
          accessibilityRole="button"
          accessibilityLabel={`Edit ${todo.title}`}
        >
          <Icon name="pencil-outline" size={16} style={styles.editIcon} />
        </Hoverable>
      </View>
    );
  },
);

TodoRow.displayName = 'TodoRow';
